import type { FieldSpec, FieldValue } from "./formFields";

/**
 * The label-and-control row, shared by the model-driven `Form` component and the command
 * dialog. Only the presentation is shared: `Form` submits through `interactive.ts` with a tool
 * call id, while a command dialog runs before any turn exists and has no call to answer.
 */
export function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
	return (
		<label className="form-field">
			<span className="form-field-label">{label}</span>
			{children}
			{hint && <span className="form-field-hint">{hint}</span>}
		</label>
	);
}

/** The five controls that need nothing but a value and a setter. `file` is not one of them. */
export function BasicControl({
	field,
	value,
	disabled,
	onChange,
}: {
	field: Pick<FieldSpec, "type" | "options">;
	value: FieldValue;
	disabled: boolean;
	onChange: (value: FieldValue) => void;
}): React.ReactNode {
	const text = typeof value === "string" ? value : "";
	switch (field.type) {
		case "textarea":
			return <textarea rows={3} value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
		case "number":
			return <input type="number" value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
		case "select":
			return (
				<select value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
					{(field.options ?? []).map((opt) => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</select>
			);
		case "checkbox":
			return (
				<input type="checkbox" checked={typeof value === "boolean" ? value : false} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
			);
		case "text":
		default:
			return <input type="text" value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
	}
}
